import { useState, useEffect, createContext, useContext } from 'react'

export const getDomain = (url?: string): string | undefined => {
  try {
    if (!url) return undefined

    try {
      return new URL(url).hostname?.toLowerCase()
    } catch {
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return new URL(`https://${url}`).hostname?.toLowerCase()
      }
      throw new Error('Invalid URL format')
    }
  } catch (error) {
    console.error('getDomain error:', error)
    return undefined
  }
}
// Define types for our favicon data
interface FaviconData {
  url?: string
  app: string
  title?: string
  bundleId?: string
  appIcon?: string
  getAppIcon?: (appName: string) => Promise<string | null>
}

interface FaviconProps {
  data: FaviconData
}

interface FaviconContextType {
  promises: Record<string, string>
  updatePromise: (key: string, value: string) => void
}

// Create a context for sharing favicon data across component instances
const FaviconContext = createContext<FaviconContextType>({
  promises: {},
  updatePromise: () => {},
})

// Custom hook to use the favicon context
function useFaviconContext() {
  return useContext(FaviconContext)
}

export function Favicon({ data }: FaviconProps) {
  const { promises, updatePromise } = useFaviconContext()
  const [domain, setDomain] = useState('')
  const [appIcon, setAppIcon] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const isWebsite = Boolean(data?.url)
  // Style objects for images
  const imageStyle = {
    width: '1.125rem',
    height: '1.125rem',
    margin: '0.125rem',
    borderRadius: '0.25rem',
    left: 1,
    flexShrink: 0,
  }

  // Set domain from URL
  useEffect(() => {
    if (isWebsite && data.url) {
      try {
        // Use the imported getDomain function
        const extractedDomain = getDomain(data.url)
        if (extractedDomain) {
          setDomain(extractedDomain)
        } else {
          setHasError(true)
        }
      } catch (error) {
        console.error('Error parsing URL:', data.url)
        // Fallback to a default domain if URL parsing fails
        setHasError(true)
      }
    }
  }, [data.url, isWebsite])

  // App icon loading effect
  useEffect(() => {
    // Only run for app icons, not site favicons
    if (isWebsite) {
      setIsLoading(false)
      return
    }

    const loadAppIcon = async () => {
      const bundleId = data?.bundleId
      const app = data.app

      if (!bundleId && !app) {
        setHasError(true)
        setIsLoading(false)
        return
      }

      if (!promises[bundleId || app || '']) {
        try {
          setIsLoading(true)
          const iconUrl = await fetchAppIcon(app)
          if (iconUrl) {
            updatePromise(bundleId || app || '', iconUrl)
            setAppIcon(iconUrl)
          } else {
            setHasError(true)
          }
          setIsLoading(false)
        } catch (error) {
          setHasError(true)
          setIsLoading(false)
          console.error(error)
        }
      } else {
        const appIcon = promises[bundleId || app || '']
        if (appIcon) {
          setAppIcon(appIcon)
          setIsLoading(false)
        }
      }
    }

    loadAppIcon()
  }, [data?.bundleId, data?.app, isWebsite, promises, updatePromise])

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.src.includes('chrome.com') || promises[domain]) return
    setHasError(true)
  }

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.src.includes('chrome.com') || promises[domain]) return

    if (e.currentTarget.naturalWidth === 1 && !promises[domain] && !promises['favicon:' + domain]) {
      setHasError(true)
      return
    }

    if (
      e.currentTarget.naturalWidth === 16 &&
      !e.currentTarget.src.includes('chrome.com') &&
      !e.currentTarget.src.includes('virginatlantic') &&
      e.currentTarget.src !==
        'https://' +
          domain
            .split('.')
            .slice(domain.split('.').length > 2 ? -3 : -2)
            .join('.') +
          '/favicon.ico' &&
      !promises[domain]
    ) {
      setHasError(true)
    }
  }

  // Helper function to get app icon
  const fetchAppIcon = async (app: string): Promise<string | null> => {
    try {
      return (await data.getAppIcon?.(app)) || null
    } catch (error) {
      console.error(error)
      return null
    }
  }

  // Render site favicon
  if (isWebsite) {
    const faviconUrl = promises[domain]
      ? '/logos/chrome.png'
      : `https://${
          promises['favicon:' + domain] ||
          `www.google.com/${
            domain?.includes('mail.google.com') ? 'a/cpanel/gmail.com/images/favicon.ico' : 's2/favicons'
          }?sz=64&domain_url=`
        }${
          promises['favicon:' + domain]
            ? ''
            : promises[domain] ||
              (domain &&
                domain
                  .split('.')
                  .slice(domain.split('.').length > 2 ? -3 : -2)
                  .join('.')) ||
              (data?.title?.includes('Untracked') || data?.url?.includes('devtools') ? 'chrome' : data?.app) + '.com'
        }`

    return <img alt="" src={faviconUrl} style={imageStyle} onError={handleImageError} onLoad={handleImageLoad} />
  }

  // App icon rendering - loading state
  if (isLoading) {
    return <div style={imageStyle} className="bg-skeleton relative animate-pulse rounded-md" />
  }

  // App icon rendering - error state
  if (hasError || !appIcon || (typeof appIcon === 'string' && appIcon.startsWith('No '))) {
    return (
      <div style={imageStyle} className="bg-element flex items-center justify-center rounded-md">
        {(data?.app || data?.title || '')?.slice(0, 1)}
      </div>
    )
  }

  // App icon rendering - success state
  return <img src={appIcon} alt="app icon" className={''} style={imageStyle} />
}
